import { forwardRef } from "react";
import { motion, useTransform } from "framer-motion";
import { useViewEvent } from "@/lib/analytics";

function Act({ p, range, className = "", children, testId }) {
    const [a, b] = range;
    const f = Math.min(0.05, (b - a) * 0.22);
    const opacity = useTransform(p, [a, a + f, b - f, b], [0, 1, 1, 0]);
    const y = useTransform(p, [a, a + f, b - f, b], [48, 0, 0, -48]);
    return (
        <motion.div
            data-testid={testId}
            style={{ opacity, y }}
            className={`pointer-events-none absolute ${className}`}
        >
            {children}
        </motion.div>
    );
}

function Overline({ children }) {
    return (
        <p className="mb-5 text-[11px] font-bold tracking-[0.28em] text-[#059669]">{children}</p>
    );
}

function ResultCard({ title, cLine, tLine, tone, testId }) {
    return (
        <div
            data-testid={testId}
            className="rounded-2xl border border-slate-200/80 bg-white/85 p-4 shadow-[0_8px_30px_-12px_rgba(10,25,47,0.15)] backdrop-blur-md"
        >
            <div className="mb-3 flex items-center justify-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                <div className="flex flex-col items-center gap-1.5">
                    <span className="text-[9px] font-bold text-slate-400">C</span>
                    <div className={`h-1 w-10 rounded-full ${cLine ? "bg-red-500" : "bg-slate-200"}`} />
                </div>
                <div className="flex flex-col items-center gap-1.5">
                    <span className="text-[9px] font-bold text-slate-400">T</span>
                    <div className={`h-1 w-10 rounded-full ${tLine ? "bg-red-500" : "bg-slate-200"}`} />
                </div>
            </div>
            <p className={`text-center text-xs font-extrabold tracking-widest ${tone}`}>{title}</p>
        </div>
    );
}

const Experience = forwardRef(function Experience({ progress }, ref) {
    const viewRef = useViewEvent("view_product");
    return (
        <section id="producto" ref={ref} data-testid="experience-section" className="relative h-[560vh]">
            <div id="tecnologia" className="absolute left-0 top-[30%]" />
            <div ref={viewRef} className="sticky top-0 h-screen overflow-hidden supports-[height:100svh]:h-[100svh]">
                {/* Act 1 — showcase rotation */}
                <Act p={progress} range={[0.02, 0.24]} testId="act-showcase" className="left-6 top-[7%] max-w-xl md:left-16 md:top-[16%] lg:left-24">
                    <Overline>LYHER® ORAL FLUID DRUG TEST CUBE</Overline>
                    <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-[#0A192F] sm:text-5xl lg:text-6xl">
                        UNA MUESTRA.
                        <br />
                        UNA PLATAFORMA.
                        <br />
                        <span className="text-[#57A632]">UNA LECTURA VISUAL.</span>
                    </h2>
                </Act>

                {/* Act 2 — exploded view */}
                <Act p={progress} range={[0.27, 0.41]} testId="act-exploded" className="left-6 top-[7%] max-w-xl md:left-16 md:top-[12%] lg:left-24">
                    <Overline>VISTA EXPANDIDA DEL DISPOSITIVO</Overline>
                    <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-[#0A192F] sm:text-5xl lg:text-6xl">
                        INGENIERÍA SIMPLE.
                        <br />
                        <span className="text-[#57A632]">INFORMACIÓN CLARA.</span>
                    </h2>
                    <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-500 sm:text-base">
                        Cada componente del Cube está diseñado para un procedimiento de screening rápido, visual y
                        verificable.
                    </p>
                </Act>

                {/* Act 3 — saturation indicator */}
                <Act p={progress} range={[0.55, 0.75]} testId="act-saturation" className="left-6 top-[7%] max-w-md md:left-16 md:top-[16%] lg:left-24">
                    <Overline>INDICADOR DE SATURACIÓN</Overline>
                    <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-[#0A192F] sm:text-5xl">
                        SABES CUÁNDO LA MUESTRA{" "}
                        <span className="text-[#57A632]">ESTÁ LISTA.</span>
                    </h2>
                    <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-500 sm:text-base">
                        El colector incorpora un indicador visual de saturación que permite verificar la recolección
                        de la muestra.
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                        <span className="h-3 w-3 rounded-full border border-slate-300 bg-white" />
                        <span className="h-px w-10 bg-slate-300" />
                        <span className="h-3 w-3 rounded-full bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.5)]" />
                        <span className="ml-2 text-[11px] font-bold tracking-[0.2em] text-slate-400">
                            BLANCO → ROJO
                        </span>
                    </div>
                </Act>

                {/* Act 4 — results reading */}
                <Act p={progress} range={[0.79, 0.985]} testId="act-results" className="inset-x-6 top-[5%] mx-auto max-w-3xl md:top-[12%]">
                    <div className="text-center">
                        <Overline>ZONA DE LECTURA</Overline>
                        <h2 className="font-display text-4xl font-extrabold tracking-tighter text-[#0A192F] sm:text-5xl">
                            UNA LECTURA <span className="text-[#57A632]">VISUAL.</span>
                        </h2>
                    </div>
                    <div className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-3">
                        <ResultCard title="NEGATIVO" cLine tLine tone="text-[#059669]" testId="result-negativo" />
                        <ResultCard title="NO NEGATIVO" cLine tLine={false} tone="text-[#F97316]" testId="result-positivo" />
                        <ResultCard title="INVÁLIDO" cLine={false} tLine tone="text-slate-400" testId="result-invalido" />
                    </div>
                    <p className="mx-auto mt-6 max-w-xl text-center text-[11px] leading-relaxed text-slate-400">
                        Los resultados de screening deben interpretarse de acuerdo con las instrucciones de uso y
                        confirmarse cuando corresponda mediante métodos analíticos apropiados.
                    </p>
                </Act>
            </div>
        </section>
    );
});

export default Experience;
